<script setup>
import {
  useHead,
  useScrollTopOnMounted,
  useRoute,
  useSanityQuery,
  computed,
} from '#imports';
import { getCommonMetaTags } from '~/utils/get-common-meta-tags';
import { getQuery as getDonationPage } from '~/groq-queries/donation-page';
import { useImageSource } from '~/composables/use-image-source';
import PageCover from '~/components/news-page/page-cover/page-cover.vue';

useScrollTopOnMounted();

const route = useRoute();

const defaultTitle = 'Act now – DOXA';
const defaultDescription =
  'Get the latest news, updates, and insights from DOXA';

const contentQuery = getDonationPage();
const { data: pageContent } = await useSanityQuery(contentQuery);

const title = computed(() => pageContent.value?.seoTitle || defaultTitle);
const description = computed(
  () => pageContent.value?.seoDescription || defaultDescription
);

const ogImageUrl = computed(() =>
  useImageSource({
    image: pageContent.value.ogImage,
  })
);

useHead({
  title,
  meta: getCommonMetaTags({
    path: route.fullPath,
    ogType: 'website',
    title: title.value,
    description: description.value,
    ogImageUrl: ogImageUrl.value,
  }),
});

function scrollToElement(elementId) {
  const element = document.getElementById(elementId);
  element.scrollIntoView({ block: 'start', behavior: 'smooth' });
}
</script>

<template>
  <LayoutStaticPage :is-colored-logo="true">
    <div class="donation-page">
      <div class="donation-page__cover">
        <PageCover
          :image-src="pageContent.image"
          :title-image="pageContent.titleImage"
          type="withGradient"
        />
      </div>
      <div v-if="pageContent.lead" class="donation-page__lead">
        {{ pageContent.lead }}
        <ButtonComponent
          button-caption="Stand with us"
          size="large"
          @click="scrollToElement('donationForm')"
        />
      </div>

      <div class="donation-page__container">
        <ArticleContent :content="pageContent" is-static-page></ArticleContent>
        <div id="donationForm" class="donation-page__payment-form">
          <DonationBlockPayment is-english />
        </div>
        <GridComponent
          v-if="pageContent.mediaCards.length > 0"
          :elements="pageContent.mediaCards"
          is-small-cards
          title="More about DOXA"
        >
          <template #element="{ element }">
            <MediaCard :item="element" />
          </template>
        </GridComponent>
        <div class="donation-page__link">
          Here is the
          <BaseExternalLink href="https://en.wikipedia.org/wiki/DOXA_Magazine"
            >Wiki page</BaseExternalLink
          >
          about us!
        </div>
      </div>
    </div>
  </LayoutStaticPage>
</template>

<style lang="postcss" scoped>
.donation-page {
  position: relative;
  z-index: 2;

  @media (--small-vp) {
    & .button {
      font-size: 16px;
      max-width: 300px;
    }
  }

  &__lead {
    font-size: var(--h5-font-size);
    font-family: var(--body-font-family);
    line-height: 32px;
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    flex-direction: column;
    max-width: 920px;
    margin: 30px auto 30px;
    text-align: center;

    @media (--tablet-vp) {
      font-size: 18px;
      line-height: 24px;
      padding: 0 12px;
    }
  }

  &__container {
    max-width: var(--content-narrow-max-width);
    margin: 24px auto 80px auto;
    padding: 0;

    @media (--tablet-vp) {
      padding: 0 12px;
    }
  }

  &__payment-form {
    display: block;
    padding: 90px 0;

    @media (--tablet-vp) {
      padding: 36px 0;
    }
  }

  &__form {
    margin-bottom: 80px;
  }

  &__link {
    font-size: 18px;
    margin: 40px 0;
    text-align: center;
  }
}
</style>
